/**
 * @file Local-storage persistence for anonymous and reconciliation-pending
 * legal consent state.
 *
 * Storage key: `wmart.legal-consent.v1`. The shape is intentionally
 * forward-compatible: unknown extra keys are preserved on read, and reads
 * gracefully no-op when `localStorage` is unavailable (SSR, tracking-prevention
 * modes, sandboxed iframes).
 */
"use client";

import {
  LEGAL_CONSENT_DOCUMENT_IDS,
  type LegalConsentDocumentId,
} from "@wmart/shared-types";

export const LEGAL_CONSENT_STORAGE_KEY = "wmart.legal-consent.v1";

/** A locally persisted consent acceptance source. */
export type LocalLegalConsentSource =
  | "anonymous"
  | "authenticated"
  | "guest_reconciliation";

export interface LocalLegalConsentSnapshot {
  acceptedVersions: Partial<Record<LegalConsentDocumentId, string>>;
  acceptedAt: string | null;
  source: LocalLegalConsentSource | null;
}

const EMPTY_SNAPSHOT: LocalLegalConsentSnapshot = {
  acceptedVersions: {},
  acceptedAt: null,
  source: null,
};

function getStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readLocalLegalConsent(): LocalLegalConsentSnapshot {
  const storage = getStorage();
  if (!storage) return { ...EMPTY_SNAPSHOT };
  const raw = storage.getItem(LEGAL_CONSENT_STORAGE_KEY);
  if (!raw) return { ...EMPTY_SNAPSHOT };
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return { ...EMPTY_SNAPSHOT };
    const obj = parsed as Record<string, unknown>;
    const accepted: Partial<Record<LegalConsentDocumentId, string>> = {};
    const rawAccepted = obj.acceptedVersions;
    if (rawAccepted && typeof rawAccepted === "object") {
      for (const id of LEGAL_CONSENT_DOCUMENT_IDS) {
        const v = (rawAccepted as Record<string, unknown>)[id];
        if (typeof v === "string") accepted[id] = v;
      }
    }
    const acceptedAt = typeof obj.acceptedAt === "string" ? obj.acceptedAt : null;
    const source =
      obj.source === "anonymous" ||
      obj.source === "authenticated" ||
      obj.source === "guest_reconciliation"
        ? obj.source
        : null;
    return { acceptedVersions: accepted, acceptedAt, source };
  } catch {
    return { ...EMPTY_SNAPSHOT };
  }
}

export function writeLocalLegalConsent(
  snapshot: LocalLegalConsentSnapshot,
): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(LEGAL_CONSENT_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // Quota or privacy-mode failure — fall through silently.
  }
}

export function clearLocalLegalConsent(): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(LEGAL_CONSENT_STORAGE_KEY);
  } catch {
    // ignore
  }
}
